"use client";

import {
  Button,
  Fieldset,
  Field,
  Input,
  Text,
  Box,
  Switch,
  HStack,
  VStack,
} from "@chakra-ui/react";
import { useUserChoices } from "@/state/globalStore";
import {
  type PostSituationRequest,
  type Situation,
  type MindstringPrototype,
  type PutSituationRequest,
} from "@/generated/fastapi";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import TOOLTIPS from "@/shared/config/tooltips";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import { showSuccessToast, showWarningToast } from "@/shared/feedback";
import { actRevalidatePrivateLayout } from "@/app/private-shell";
import ReactDatePicker from "react-datepicker";
import { format } from "date-fns";
import { enGB } from "date-fns/locale"; // Add this import
import "react-datepicker/dist/react-datepicker.min.css"; // use the shipped minified CSS to avoid dev source-map noise
import GenericSelector from "@/shared/ui/controls/GenericSelector";
import { useOptionItems } from "@/shared/ui/controls/useOptionItems";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping";
import useCreateSituationMutation from "../mutations/useCreateSituationMutation";
import useSituationsQuery from "../queries/useSituationsQuery";
import useUpdateSituationMutation from "../mutations/useUpdateSituationMutation";

/// --------------------------------
/// Utility Functions
/// --------------------------------

const formatDateForBackend = (date: Date | null | string) => {
  if (!date) return null;
  const parsedDate = typeof date === "string" ? new Date(date) : date;
  if (isNaN(parsedDate.getTime())) return null; // Handle invalid dates
  return parsedDate.toISOString().split("T")[0];
};

/// --------------------------------
/// Form Schema and Types
/// --------------------------------
const createSituationSchema = (
  isCurrentlyWorking: boolean,
  hasStarted: boolean,
) => {
  return z
    .object({
      position: z
        .string()
        .min(2, "Role or title must be at least 2 characters")
        .max(100, "Role or title is too long")
        .trim(),

      context: z
        .string()
        .min(2, "Context must be at least 2 characters")
        .max(100, "Context is too long")
        .trim(),
      prototypes: z //! replacing `categories`
        .array(z.string())
        .min(1, "Choose at least one perspective"),

      // Conditionally require dateFrom based on hasStarted
      dateFrom: hasStarted
        ? z.date({
            required_error: "Start date is required",
          })
        : z.date().nullable().optional(), // Allow null if hasStarted is false

      // Conditionally require dateTo based on hasStarted and isCurrentlyWorking
      dateTo: hasStarted
        ? isCurrentlyWorking
          ? z.date().nullable().optional() // Not required if currently working
          : z.date({
              required_error: "End date is required if not currently working",
            })
        : z.date().nullable().optional(), // Allow null if hasStarted is false
      location: z.string().max(100, "Location is too long").trim().optional(),
    })
    .refine(
      (data) => !data.dateFrom || !data.dateTo || data.dateTo >= data.dateFrom,
      {
        message: "End date cannot be before start date",
        path: ["dateTo"], // Target the `dateTo` field for the error
      },
    );
};

type SubmitSituationFormData = z.infer<
  ReturnType<typeof createSituationSchema>
>;

/// --------------------------------
/// Hook: useSubmitSituationForm
/// --------------------------------

function useSubmitSituationForm() {
  const createSituation = useCreateSituationMutation();
  const updateSituation = useUpdateSituationMutation();
  const situationsQuery = useSituationsQuery();

  async function handleSituationSubmitPost(
    requst: PostSituationRequest,
    onSuccess?: (situation: Situation) => void,
  ) {
    try {
      const response = await createSituation.mutateAsync(requst);

      if (response) {
        if ((situationsQuery.data?.length ?? 0) === 0) {
          await actRevalidatePrivateLayout();
        }
        onSuccess?.(response);
        showSuccessToast("Situation saved");
      }
    } catch {
      showWarningToast("Failed to save situation");
    }
  }

  async function handleSituationSubmitPut(
    requst: PutSituationRequest,
    onSuccess?: (situation: Situation) => void,
  ) {
    try {
      const response = await updateSituation.mutateAsync(requst);

      if (response) {
        onSuccess?.(response);
        showSuccessToast("Situation updated");
      }
    } catch {
      showWarningToast("Failed to update situation");
    }
  }
  return {
    handleSituationSubmitPost,
    handleSituationSubmitPut,
    isSubmittingMutation:
      createSituation.isPending || updateSituation.isPending,
  };
}

/// --------------------------------
/// Hook: useDelectSituationChange
/// --------------------------------

function useDetectSituationChange(
  situationToEdit: Situation | null | undefined,
  hasStarted: boolean,
  isCurrentlyWorking: boolean,
) {
  const detectChanges = (data: SubmitSituationFormData): boolean => {
    if (!situationToEdit) return true; // New situation always has changes

    // Compare each field
    const hasChanges =
      data.position !== situationToEdit.position ||
      data.context !== situationToEdit.context ||
      // JSON.stringify(data.categories) !==
      //   JSON.stringify(situationToEdit.categories) ||
      isCurrentlyWorking !== situationToEdit.is_current ||
      hasStarted !== situationToEdit.has_started ||
      data.location !== situationToEdit.location ||
      (hasStarted &&
        (data.dateFrom?.toISOString() !== situationToEdit.date_from ||
          (!isCurrentlyWorking &&
            data.dateTo?.toISOString() !== situationToEdit.date_to)));

    return hasChanges;
  };

  return { detectChanges };
}

interface SituationSubmitFormProps {
  situationToEdit?: Situation | null;
  allowFutureSituations?: boolean; // New prop to control future situations
  onSituationSubmittedSuccess?: (situation: Situation) => void;
  closeOnSuccess?: boolean;
  showOrientation?: boolean;
}

export default function SituationSubmitForm({
  situationToEdit,
  allowFutureSituations = false, // Design choice for now
  onSituationSubmittedSuccess,
  closeOnSuccess = true,
  showOrientation = true,
}: SituationSubmitFormProps) {
  const { resetInterimState } = useUserChoices();
  const {
    handleSituationSubmitPost,
    handleSituationSubmitPut,
    isSubmittingMutation,
  } = useSubmitSituationForm();
  const { mindstringPrototypesSlangItems } = useOptionItems(); // Added for GenericSelector

  // Local states to support UI logic
  const [hasStarted, setHasStarted] = useState<boolean>(
    // load initial value from situationToEdit if editing, else assume started=true (for new situation)
    situationToEdit ? situationToEdit.has_started : true,
  );
  const [isCurrentlyWorking, setIsCurrentlyWorking] = useState<boolean>(
    // load initial value from situationToEdit if editing, else assume currently working=true (for new situation)
    situationToEdit ? situationToEdit.is_current : true,
  );
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [selectedSituationPrototypes, setSelectedSituationPrototypes] =
    useState<MindstringPrototype[]>(situationToEdit?.prototypes || []); // Load up existing prototypes if editing
  const situationModeLabel = situationToEdit
    ? PROFILE_SHAPING_LABELS.editSituation
    : PROFILE_SHAPING_LABELS.addSituation;
  const situationStatusLabel = !hasStarted
    ? "Future"
    : isCurrentlyWorking
      ? "In progress"
      : "Past";
  const situationStatusColor = !hasStarted
    ? "yellow"
    : isCurrentlyWorking
      ? "green"
      : "gray";
  const prototypeCount = selectedSituationPrototypes.length;
  const prototypeCountLabel =
    prototypeCount === 1
      ? "1 perspective"
      : `${prototypeCount} perspectives`;
  const orientationText = situationToEdit
    ? "Update this career-relevant moment, its time period, and the perspectives it can anchor."
    : PROFILE_SHAPING_MESSAGES.situationLong;

  // Use the dynamic schema creator to reflect current state
  const situationSchema = createSituationSchema(isCurrentlyWorking, hasStarted);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    setError,
    clearErrors,
    control,
  } = useForm<SubmitSituationFormData>({
    resolver: zodResolver(situationSchema),
    // If editing, populate form with existing data, else use defaults
    defaultValues: {
      position: situationToEdit?.position || "",
      context: situationToEdit?.context || "",
      // categories: situationToEdit?.categories || [], // TODO replace with situation.prototypes
      prototypes: situationToEdit?.prototypes || [], // ! replacing `categories`
      dateFrom: situationToEdit?.date_from
        ? new Date(situationToEdit.date_from)
        : undefined,
      dateTo: situationToEdit?.date_to
        ? new Date(situationToEdit.date_to)
        : undefined,
      location: situationToEdit?.location || "",
    },
    mode: "onSubmit", // re-validate when the form is submitted NOT while typing
  });

  const watchedDateFrom = useWatch({ control, name: "dateFrom" });
  const watchedDateTo = useWatch({ control, name: "dateTo" });

  // Add effect to handle dateTo validation when dateFrom changes
  useEffect(() => {
    // If we have both dates, make sure dateTo is not before dateFrom
    if (watchedDateFrom && watchedDateTo && watchedDateTo < watchedDateFrom) {
      setError("dateTo", {
        type: "validate",
        message: "End date cannot be before start date",
      });
    }
  }, [watchedDateFrom, watchedDateTo, setError]);

  // Add effects to handle validation when form state changes
  useEffect(() => {
    if (!hasStarted && (watchedDateFrom || watchedDateTo)) {
      clearErrors(["dateFrom", "dateTo"]);
      setValue("dateFrom", null);
      setValue("dateTo", null);
    }
  }, [hasStarted, watchedDateFrom, watchedDateTo, clearErrors, setValue]);

  // Sync selectedSituationPrototypes with React Hook Form
  useEffect(() => {
    setValue("prototypes", selectedSituationPrototypes);
  }, [selectedSituationPrototypes, setValue]);

  // Detect changes hook
  const { detectChanges } = useDetectSituationChange(
    situationToEdit,
    hasStarted,
    isCurrentlyWorking,
  );

  // Function to execute after successful submission
  const onSuccessCallback = (savedSituation: Situation) => {
    // Reset form values using React Hook Form's setValue
    setValue("position", "");
    setValue("context", "");
    // setValue("categories", []);
    setValue("prototypes", []);
    setValue("dateFrom", null);
    setValue("dateTo", null);
    setValue("location", "");

    // Reset local state (for UI and other logic)
    // setSelectedSituationCategories([]);
    setHasStarted(true);
    setIsCurrentlyWorking(false);
    setDateFrom(null);
    setDateTo(null);

    onSituationSubmittedSuccess?.(savedSituation);

    if (closeOnSuccess) {
      // Close the drawer
      resetInterimState(situationToEdit ? "edit_situation" : "add_situation");
    }
  };

  const onSubmit = async (data: SubmitSituationFormData) => {
    // Check for changes
    if (situationToEdit && !detectChanges(data)) {
      showWarningToast("No changes detected");
      return;
    }

    // Prepare request data (common for both POST and PUT except situation_id)
    const situationData = {
      ...(situationToEdit && { situation_id: situationToEdit.id }), // Include ID for PUT
      position: data.position,
      context: data.context,
      // categories: data.categories as SituationCategory[], // TODO to be removed ....
      prototypes: selectedSituationPrototypes, // sending list of MindstringPrototype directly
      has_started: hasStarted,
      date_from: formatDateForBackend(
        hasStarted && data.dateFrom ? data.dateFrom : null,
      ),
      date_to: formatDateForBackend(
        hasStarted && !isCurrentlyWorking && data.dateTo ? data.dateTo : null,
      ),
      is_current: hasStarted && isCurrentlyWorking,
      location: data.location,
    };

    // Submit
    if (situationToEdit) {
      await handleSituationSubmitPut(
        situationData as PutSituationRequest, // Cast to PutSituationRequest type
        onSuccessCallback,
      );
    } else {
      await handleSituationSubmitPost(
        situationData as PostSituationRequest, // Cast to PostSituationRequest type
        onSuccessCallback,
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Fieldset.Root size="lg" invalid={Object.keys(errors).length > 0}>
        <Fieldset.Content>
          {showOrientation ? (
            <GenericDrawerInfo
              description={orientationText}
              items={[
                { label: situationModeLabel, colorPalette: "blue" },
                {
                  label: situationStatusLabel,
                  colorPalette: situationStatusColor,
                },
                {
                  label:
                    prototypeCount > 0
                      ? prototypeCountLabel
                      : "Choose perspectives",
                  colorPalette: prototypeCount > 0 ? "purple" : "orange",
                },
              ]}
            />
          ) : null}

          {/* Position Field */}
          <Field.Root invalid={!!errors.position}>
            <Field.Label>Role or title</Field.Label>
            <Input
              id="situation-position"
              {...register("position")}
              placeholder="Role, project, course, achievement, or life moment"
            />
            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Situation.Position}
            </Field.HelperText>
            {errors.position && (
              <Field.ErrorText>{errors.position.message}</Field.ErrorText>
            )}
          </Field.Root>

          {/* Context Field */}
          <Field.Root invalid={!!errors.context}>
            <Field.Label>Where it happened</Field.Label>
            <Input
              id="situation-context"
              {...register("context")}
              placeholder="Company, school, client, team, place, or context"
            />
            <Field.HelperText whiteSpace="pre-line">
              {TOOLTIPS.Situation.Context}
            </Field.HelperText>
            {errors.context && (
              <Field.ErrorText>{errors.context.message}</Field.ErrorText>
            )}
          </Field.Root>

          {/* OFF BY DESIGN - DO NOT ALLOW USERS CREATING FUTURE SITUATIONS */}
          {allowFutureSituations && (
            <Box p="3" my="3" borderRadius="md">
              <HStack justifyContent="space-between" alignItems="center">
                <VStack alignItems="flex-start" spaceY={0}>
                  <Text fontWeight="medium">Situation status</Text>
                </VStack>

                <Switch.Root
                  checked={hasStarted}
                  onCheckedChange={(details) => setHasStarted(details.checked)}
                  aria-label="Job status toggle"
                >
                  <Switch.HiddenInput />
                  <Switch.Label ml="2">
                    {hasStarted ? "Started" : "Future"}
                  </Switch.Label>
                  <Switch.Control />
                </Switch.Root>
              </HStack>
              <Text
                color="moveon.text.muted"
                fontSize="sm"
                mt="2"
                whiteSpace="pre-line"
              >
                {TOOLTIPS.Situation.Status}
              </Text>
            </Box>
          )}

          {/* Time period fields */}
          {hasStarted ? (
            <Fieldset.Root mt="3">
              <Fieldset.Legend>Time Period</Fieldset.Legend>
              <Fieldset.Content>
                {/* Grid layout for date fields and toggles */}
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                  gap="3"
                  p="3"
                  borderRadius="md"
                >
                  {/* Start Date - Top Left */}
                  <Box>
                    <Controller
                      name="dateFrom"
                      control={control}
                      render={({ field }) => (
                        <Field.Root invalid={!!errors.dateFrom}>
                          <Field.Label>From Date</Field.Label>
                          <ReactDatePicker
                            selected={field.value}
                            onChange={(date) => {
                              field.onChange(date);
                              setDateFrom(date as Date);
                            }}
                            dateFormat="dd/MM/yyyy"
                            locale={enGB}
                            customInput={
                              <Input
                                id="situation-date-from"
                                name="dateFrom"
                                width="full"
                                size="sm"
                              />
                            }
                            withPortal
                          />
                          {errors.dateFrom && (
                            <Text color="red.500" fontSize="sm" mt="1">
                              {errors.dateFrom.message}
                            </Text>
                          )}
                        </Field.Root>
                      )}
                    />
                  </Box>

                  {/* "Currently Working" Toggle - Top Right */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    height="full"
                    pl="2"
                  >
                    <Switch.Root
                      checked={isCurrentlyWorking}
                      onCheckedChange={(details) => {
                        setIsCurrentlyWorking(details.checked);
                        if (details.checked) {
                          // Clear end date if currently working
                          setValue("dateTo", null);
                          setDateTo(null);
                          clearErrors("dateTo");
                        }
                      }}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                      <Switch.Label fontWeight="medium" ml="2">
                        {isCurrentlyWorking
                          ? "Still in progress"
                          : "Ended in past"}
                      </Switch.Label>
                    </Switch.Root>
                  </Box>

                  {/* End Date - Bottom Left and Summary - Bottom Right (Only visible if not currently working) */}
                  {!isCurrentlyWorking && (
                    <>
                      <Box>
                        <Controller
                          name="dateTo"
                          control={control}
                          render={({ field }) => (
                            <Field.Root invalid={!!errors.dateTo}>
                              <Field.Label>To Date</Field.Label>
                              <ReactDatePicker
                                selected={field.value}
                                onChange={(date) => {
                                  field.onChange(date);
                                  setDateTo(date as Date);
                                }}
                                dateFormat="dd/MM/yyyy"
                                locale={enGB}
                                customInput={
                                  <Input
                                    id="situation-date-to"
                                    name="dateTo"
                                    width="full"
                                    size="sm"
                                  />
                                }
                                minDate={dateFrom ? dateFrom : undefined}
                                withPortal
                              />
                              {errors.dateTo && (
                                <Text color="red.500" fontSize="sm" mt="1">
                                  {errors.dateTo.message}
                                </Text>
                              )}
                            </Field.Root>
                          )}
                        />
                      </Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                        pl="2"
                      >
                        <Box
                          fontSize="sm"
                          fontWeight="medium"
                          color="gray.600"
                          p="1"
                          borderRadius="md"
                          bg="gray.100"
                        >
                          {dateFrom ? format(dateFrom, "MMM yyyy") : "–"} —{" "}
                          {dateTo ? ` ${format(dateTo, "MMM yyyy")}` : " –"}
                        </Box>
                      </Box>
                    </>
                  )}

                  {/* If currently working, show summary with "Present" */}
                  {isCurrentlyWorking && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      pl="2"
                    >
                      <Box
                        fontSize="sm"
                        fontWeight="medium"
                        color="green.600"
                        p="1"
                        borderRadius="md"
                        bg="green.50"
                      >
                        {dateFrom ? format(dateFrom, "MMM yyyy") : "–"} —{" "}
                        <span style={{ fontWeight: "bold" }}> Present</span>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Fieldset.Content>
            </Fieldset.Root>
          ) : (
            // When job hasn't started, show a message
            <Box
              mt="3"
              p="3"
              borderRadius="md"
              bg="yellow.50"
              // @ts-ignore: Chakra prop and value definition required for layout correctness
              borderWidth="1px"
              borderColor="yellow.200"
            >
              <Text fontSize="sm" color="yellow.800">
                This is a future situation. No date.
              </Text>
            </Box>
          )}

          {/* Location field */}
          <Field.Root mt="4" invalid={!!errors.location}>
            <Field.Label>Location (optional)</Field.Label>
            <Input
              id="situation-location"
              {...register("location")}
              placeholder="City, country, remote, or hybrid"
            />
            {errors.location && (
              <Field.ErrorText>{errors.location.message}</Field.ErrorText>
            )}
          </Field.Root>
          {/* Perspective selector field*/}
          <Field.Root invalid={!!errors.prototypes}>
            <Field.Label>Perspectives</Field.Label>
            <GenericSelector<MindstringPrototype>
              previewItems={mindstringPrototypesSlangItems}
              stateObject={selectedSituationPrototypes}
              stateObjectSetter={setSelectedSituationPrototypes}
              mode="select"
              inputId="situation-prototypes"
              inputName="prototypes"
              placeholder="Choose perspectives"
              disabled={isSubmitting || isSubmittingMutation}
              allowMultiple={true} // Allow multiple selection
              disableClear={true} //  Disable clear all button to enforce at least one selection
              showDescription={true} // Show description tooltips
            />
            {errors.prototypes && (
              <Field.ErrorText>{errors.prototypes.message}</Field.ErrorText>
            )}
          </Field.Root>
        </Fieldset.Content>

        {/* Submit Button */}
        <Box mt="4" display="flex" justifyContent="flex-end">
          <Button
            bg="moveon.primary"
            type="submit"
            loading={isSubmitting || isSubmittingMutation}
            loadingText="Saving situation"
          >
            <GenericIcon icon={situationToEdit ? "edit" : "add"} />
            {situationToEdit
              ? PROFILE_SHAPING_LABELS.editSituation
              : PROFILE_SHAPING_LABELS.addSituation}
          </Button>
        </Box>
      </Fieldset.Root>
    </form>
  );
}
